import { FC, useState, useMemo } from "react";
import styled from "styled-components";
import orderBy from "lodash.orderby";

import {
  LargeTh,
  MediumTh,
  Table,
  TableContent,
  Th,
  SortableTh,
} from "@/components/Table";
import ScrollableTable from "@/components/ScrollableTable";
import { SortDirection } from "@/utils/enums";
import { SortState, VideoTipResource } from "@/types/resources";
import { SrOnly } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import DistributionScheduleTipsTableRow from "./DistributionScheduleTipsTableRow";
import Button from "@/components/Button";

const StyledTable = styled(Table)`
  thead {
    th {
      background: ${({ theme }) => theme.colors.grayscaleLight} !important;
    }
  }
  th,
  td {
    padding: 12px !important;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      padding-left: 24px !important;
    }
    &:last-child {
      min-width: 56px;
      padding-right: 24px !important;
      padding: 8px 12px !important;
    }
  }
`;

const NoTipsTd = styled.td`
  padding: 0 !important;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const NoTipsText = styled(Text)`
  padding: 16px;
  position: sticky;
  left: 0;
  text-align: center;
  max-width: 1056px;
`;

const HeadingText = styled(Text)`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  text-align: left;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  > button {
    margin-left: 10px;
    height: 23px;
    padding: 4px 12px;
    font-size: 11px;
    svg {
      margin-left: 9px;
      margin-right: 0;
    }
  }
`;

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "views",
};

interface Props {
  tips: VideoTipResource[];
  releaseId: number;
  onAddTipClick?: () => void;
}

const DistributionScheduleTipsTable: FC<Props> = ({
  tips: unsortedTips,
  releaseId,
  onAddTipClick,
}) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const tips = useMemo(() => {
    if (sort.direction === SortDirection.DEFAULT) {
      return unsortedTips;
    }

    const property = sort.property as keyof VideoTipResource;

    const direction = sort.direction.toLowerCase() as "asc" | "desc";

    return orderBy(unsortedTips, (tip) => tip[property] || -1000, direction);
  }, [unsortedTips, sort]);

  return (
    <>
      <HeadingText $type="caption">
        Compilation Tips
        {!!onAddTipClick && (
          <Button $type="secondary" small onClick={onAddTipClick}>
            Add tip
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.5 1.375V9.625"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1.375 5.5H9.625"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        )}
      </HeadingText>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <MediumTh>Hack ID</MediumTh>
              <LargeTh>Hack Name</LargeTh>
              <Th>Thumbnail</Th>
              <SortableTh
                property="duration"
                title="Duration"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="compilation_timestamp"
                title="Compilation Timestamp"
                setSort={setSort}
                sort={sort}
              />
              <Th>Video Type</Th>
              <Th>Embeded Link</Th>
              <SortableTh
                property="category"
                title="Category"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="subcategory"
                title="Subcategory"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="mashup"
                title="Mashup"
                setSort={setSort}
                sort={sort}
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent isLoading={false} isError={false} hasData>
              {tips.length ? (
                <>
                  {tips.map((tip) => (
                    <DistributionScheduleTipsTableRow
                      key={tip.id}
                      tip={tip}
                      releaseId={releaseId}
                    />
                  ))}
                </>
              ) : (
                <tr>
                  <NoTipsTd colSpan={100}>
                    <NoTipsText $type="smallBold" $color="gray">
                      No tips added
                    </NoTipsText>
                  </NoTipsTd>
                </tr>
              )}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </>
  );
};

export default DistributionScheduleTipsTable;
